import {AxiosResponse} from "axios";
import {ApiBase} from "./ApiBase";
import {IApiResult} from "./AxiosWealthManagementApi";

export interface IDateRangeRow {
    startDate: string;
    endDate: string;
}

export interface ILoginSummaryRow {
    total: number;
    internal: number;
    client: number;
    thirdParty: number;
    external: number;
}

export interface IMonthlyLoginRow {
    month: string;
    internal: number;
    client: number;
    thirdParty: number;
    external: number;
    total: number;
}

export interface IMonthlyComparisonRow {
    month: string;
    total: number;
    internal: number;
    external: number;
    momPercent?: number;
    yoyPercent?: number;
}

export interface ICountryLoginRow {
    country: string;
    internal: number;
    external: number;
    total: number;
}

export interface IAreaUsageRow {
    area: string;
    internal: number;
    external: number;
    total: number;
}

export interface IAreaTrendPoint {
    month: string;
    visits: number;
}

export interface IAreaTrendRow {
    area: string;
    monthly: IAreaTrendPoint[];
}

export interface IPlanEngagementRow {
    planName: string;
    visits: number;
    visitShare: number;
    topArea: string;
}

export interface IPowerUserRow {
    user: string;
    userType: string;
    visits: number;
    topArea: string;
}

export interface IDailyLoginRow {
    date: string;
    internal: number;
    external: number;
    total: number;
    client?: number;
    thirdParty?: number;
}

export interface INewVsReturningRow {
    month: string;
    newUsers: number;
    returningUsers: number;
    total: number;
}

export interface IRetentionRow {
    period: string;
    priorUsers: number;
    retainedUsers: number;
    retentionPercent: number;
}

export interface ISessionDepthRow {
    bucket: string;
    sessionCount: number;
    percentage: number;
}

export interface IInactiveUserRow {
    user: string;
    userType: string;
    lastLogin: string;
    daysSinceLogin: number;
}

export interface IPlanFunnelRow {
    planName: string;
    totalUsers: number;
    totalPageViews: number;
    avgPagesPerUser: number;
}

export interface ICountryEngagementRow {
    country: string;
    avgPagesPerUser: number;
    totalSessions: number;
}

export interface ILoginFrequencyRow {
    bucket: string;
    userCount: number;
    percentage: number;
}

export interface IPortfolioPlanRow {
    planName: string;
    hasParisPlan: boolean;
    clientPortfolioEnabled: boolean;
    clientModelPortfolioEnabled: boolean;
    totalUsers: number;
    totalPageViews: number;
    portfolioPageViews: number;
    hasAdoptionGap: boolean;
}

export interface IClientAdoptionRow {
    planName: string;
    clientUsers: number;
    internalUsers: number;
    totalUsers: number;
    clientPercent: number;
}

export interface IPlanFeatureUsageRow {
    planName: string;
    research: number;
    clientReporting: number;
    sharedDocs: number;
    portfolio: number;
    whitePapers: number;
    marketOutlooks: number;
    totalVisits: number;
}

export interface IDocumentOpenRow {
    documentPath: string;
    documentType: string;
    documentName: string;
    opens: number;
    uniqueUsers: number;
    internalOpens: number;
    externalOpens: number;
    uniqueInternalUsers: number;
    uniqueExternalUsers: number;
}

export interface IPlanPageRow {
    page: string;
    totalHits: number;
    internalHits: number;
    clientHits: number;
    uniqueInternalUsers: number;
    uniqueClientUsers: number;
}

export interface IPlanDrilldownResponse {
    planName: string;
    totalPageViews: number;
    uniqueUsers: number;
    pages: IPlanPageRow[];
}
export interface IUserPlanPageRow {
    page: string;
    hits: number;
}

export interface IUserPlanRow {
    planName: string;
    totalHits: number;
    topPages: IUserPlanPageRow[];
}

export interface IUserDrilldownResponse {
    userEmail: string;
    totalPageViews: number;
    plansAccessed: number;
    plans: IUserPlanRow[];
}

export interface ILoginTabResponse {
    dateRange: IDateRangeRow;
    loginSummary: ILoginSummaryRow;
    monthlyLogins: IMonthlyLoginRow[];
    monthlyComparison: IMonthlyComparisonRow[];
    countryLogins: ICountryLoginRow[];
    countryEngagement: ICountryEngagementRow[];
    newVsReturning: INewVsReturningRow[];
    loginFrequency: ILoginFrequencyRow[];
    avgLoginsPerUser: number;
}

export interface IEngagementTabResponse {
    areaUsage: IAreaUsageRow[];
    areaTrends: IAreaTrendRow[];
    allAreaUsage: IAreaUsageRow[];
    internalPowerUsers: IPowerUserRow[];
    clientPowerUsers: IPowerUserRow[];
    sessionDepth: ISessionDepthRow[];
    avgSessionDepth: number;
    documentOpens: IDocumentOpenRow[];
    totalWhitePaperOpens: number;
    totalMarketOutlookOpens: number;
    contentUniqueUsers: number;
}

export interface IPlansTabResponse {
    planEngagement: IPlanEngagementRow[];
    planFunnel: IPlanFunnelRow[];
    clientAdoption: IClientAdoptionRow[];
    planFeatureUsage: IPlanFeatureUsageRow[];
    portfolioPlans: IPortfolioPlanRow[];
}

export interface IHealthTabResponse {
    retention: IRetentionRow[];
    inactiveUsers: IInactiveUserRow[];
    inactiveCount: number;
}

export class AdminAnalyticsApi {
    private static buildUrl(path: string, startDate: string, endDate: string, country?: string): string {
        let url = `/admin/analytics/${path}?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
        if (country) url += `&country=${encodeURIComponent(country)}`;
        return url;
    }

    public static getLoginTab(startDate: string, endDate: string, country?: string):
        Promise<IApiResult<ILoginTabResponse>> {
        return ApiBase.processGet(this.buildUrl("login", startDate, endDate, country), (r: AxiosResponse<ILoginTabResponse>) => r.data);
    }

    public static getEngagementTab(startDate: string, endDate: string, country?: string):
        Promise<IApiResult<IEngagementTabResponse>> {
        return ApiBase.processGet(this.buildUrl("engagement", startDate, endDate, country), (r: AxiosResponse<IEngagementTabResponse>) => r.data);
    }

    public static getPlansTab(startDate: string, endDate: string, country?: string):
        Promise<IApiResult<IPlansTabResponse>> {
        return ApiBase.processGet(this.buildUrl("plans", startDate, endDate, country), (r: AxiosResponse<IPlansTabResponse>) => r.data);
    }

    public static getHealthTab(startDate: string, endDate: string, country?: string):
        Promise<IApiResult<IHealthTabResponse>> {
        return ApiBase.processGet(this.buildUrl("health", startDate, endDate, country), (r: AxiosResponse<IHealthTabResponse>) => r.data);
    }

    public static getPlanDrilldown(planName: string, startDate: string, endDate: string, country?: string):
        Promise<IApiResult<IPlanDrilldownResponse>> {
        let url = `/admin/analytics/plan-drilldown?planName=${encodeURIComponent(planName)}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
        if (country) url += `&country=${encodeURIComponent(country)}`;
        return ApiBase.processGet(url, (r: AxiosResponse<IPlanDrilldownResponse>) => r.data);
    }

    public static getDailyLogins(startDate: string, endDate: string, country?: string):
        Promise<IApiResult<IDailyLoginRow[]>> {
        let url = `/admin/analytics/daily-logins?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
        if (country) url += `&country=${encodeURIComponent(country)}`;
        return ApiBase.processGet(url, (response: AxiosResponse<IDailyLoginRow[]>) => response.data);
    }

    public static getUserDrilldown(userEmail: string, startDate: string, endDate: string, country?: string):
        Promise<IApiResult<IUserDrilldownResponse>> {
        let url = `/admin/analytics/user-drilldown?userEmail=${encodeURIComponent(userEmail)}&startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
        if (country) url += `&country=${encodeURIComponent(country)}`;
        return ApiBase.processGet(url, (r: AxiosResponse<IUserDrilldownResponse>) => r.data);
    }
}
